import { useState, useEffect, useRef } from "react";
import { createJob, updateJob } from "../services/jobService";
import { toast } from "../utils/toast";

const STATUS_COLORS = {
  Saved:         "#6b7280",
  Applied:       "#2563eb",
  "OA":          "#0891b2",
  Interview:     "#ff9d00",
  Accepted:      "#16a34a",
  Rejected:      "#ef4444",
};

const EMPLOYMENT_TYPES = [
  "Full-time", "Part-time", "Contract",
  "Internship", "Summer Internship", "Traineeship",
  "Graduate Program", "Co-op", "Freelance",
];

export default function ApplicationModal({ onClose, onSubmit, resumes, currentUserId, initialApp }) {
  // Job fields
  const [company, setCompany]               = useState("");
  const [jobTitle, setJobTitle]             = useState("");
  const [location, setLocation]             = useState("");
  const [employmentType, setEmploymentType] = useState("Full-time");
  const [deadline, setDeadline]             = useState("");
  const [description, setDescription]       = useState("");
  const [jobUrl, setJobUrl]                 = useState("");
  const [contactName, setContactName]       = useState("");

  // Application fields
  const [status, setStatus]                 = useState("Saved");
  const [selectedResumeId, setSelectedResumeId] = useState("");

  // Status dropdown
  const [statusOpen, setStatusOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const statusRef = useRef(null);

  // Resume dropdown
  const [resumeOpen, setResumeOpen] = useState(false);
  const [resumeDropdownPos, setResumeDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const resumeRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) {
        setStatusOpen(false);
      }
      if (resumeRef.current && !resumeRef.current.contains(e.target)) {
        setResumeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (initialApp) {
      setCompany(initialApp.job?.company || "");
      setJobTitle(initialApp.job?.title || "");
      setLocation(initialApp.job?.location || "");
      setEmploymentType(initialApp.job?.employment_type || "Full-time");
      setDeadline(initialApp.job?.deadline ? initialApp.job.deadline.slice(0, 10) : "");
      setDescription(initialApp.job?.description || "");
      setJobUrl(initialApp.job?.job_url || "");
      setContactName(initialApp.job?.contact_name || "");
      setStatus(initialApp.status || "Saved");
      setSelectedResumeId(initialApp.resume_id || "");
    }
  }, [initialApp]);

  const handleSubmit = async () => {
    if (!company.trim()) { toast("Company is required"); return; }
    if (!jobTitle.trim()) { toast("Role is required"); return; }

    try {
      const jobPayload = {
        company: company.trim(),
        title: jobTitle.trim(),
        location: location.trim(),
        employment_type: employmentType,
        deadline: deadline && deadline.trim() !== "" ? deadline : null,
        description: description.trim(),
        job_url: jobUrl.trim() || null,
        contact_name: contactName.trim() || null,
        user_id: currentUserId,
      };

      let job;
      if (initialApp?.job?.id) {
        job = await updateJob(initialApp.job.id, jobPayload);
      } else {
        job = await createJob(jobPayload);
      }

      onSubmit({
        title: company.trim(),
        job_id: job.id,
        resume_id: selectedResumeId ? Number(selectedResumeId) : null,
        status,
      });
      onClose();
    } catch (err) {
      console.error(err);
      toast("Failed to save application");
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content doc-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{initialApp ? "Edit Application" : "New Application"}</h2>

        <div className="modal-section-label">Job Details</div>

        <div className="field">
          <label>Company *</label>
          <input className="input-sm" value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Google" />
        </div>

        <div className="field">
          <label>Role *</label>
          <input className="input-sm" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} placeholder="Frontend Developer" />
        </div>

        <div className="modal-row">
          <div className="field">
            <label>Location</label>
            <input className="input-sm" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Remote / NYC" />
          </div>
          <div className="field">
            <label>Type</label>
            <select className="input-sm" value={employmentType} onChange={(e) => setEmploymentType(e.target.value)}>
              {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div className="modal-row">
          <div className="field">
            <label>Deadline <span className="field-optional">(optional)</span></label>
            <input className="input-sm" type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} />
          </div>
          <div className="field">
            <label>Contact <span className="field-optional">(optional)</span></label>
            <input className="input-sm" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="Jane Smith (Recruiter)" />
          </div>
        </div>

        <div className="field">
          <label>Job URL <span className="field-optional">(optional)</span></label>
          <input className="input-sm" value={jobUrl} onChange={(e) => setJobUrl(e.target.value)} placeholder="https://..." />
        </div>

        <div className="field">
          <label>Notes <span className="field-optional">(optional)</span></label>
          <textarea className="input-sm" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Job responsibilities, requirements..." />
        </div>

        <div className="modal-section-label">Application</div>

        <div className="modal-row">
          <div className="field">
            <label>Status</label>
            <div className="status-dropdown-wrapper" ref={statusRef}>
              <button className="status-picker-trigger" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setDropdownPos({ top: r.top - 4, left: r.left }); setStatusOpen((o) => !o); }} type="button">
                <span className="status-dot" style={{ backgroundColor: STATUS_COLORS[status] }} />
                {status}
                <span className="status-picker-arrow">▾</span>
              </button>
              {statusOpen && (
                <div className="status-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, minWidth: 160, transform: "translateY(-100%)" }}>
                  {Object.entries(STATUS_COLORS).map(([s, color]) => (
                    <div
                      key={s}
                      className={`status-dropdown-item${status === s ? " active" : ""}`}
                      onClick={() => { setStatus(s); setStatusOpen(false); }}
                    >
                      <span className="status-dot" style={{ backgroundColor: color }} />
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="field">
            <label>Resume <span className="field-optional">(optional)</span></label>
            <div className="status-dropdown-wrapper" ref={resumeRef}>
              <button
                className="status-picker-trigger"
                type="button"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setResumeDropdownPos({ top: r.top - 4, left: r.left, width: r.width });
                  setResumeOpen((o) => !o);
                }}
              >
                {resumes.find(r => String(r.id) === String(selectedResumeId))?.title || "None"}
                <span className="status-picker-arrow">▾</span>
              </button>
              {resumeOpen && (
                <div className="status-dropdown" style={{ position: "fixed", top: resumeDropdownPos.top, left: resumeDropdownPos.left, width: resumeDropdownPos.width, transform: "translateY(-100%)" }}>
                  <div className="status-dropdown-item" onClick={() => { setSelectedResumeId(""); setResumeOpen(false); }}>None</div>
                  {resumes.map((r) => (
                    <div
                      key={r.id}
                      className={`status-dropdown-item${String(selectedResumeId) === String(r.id) ? " active" : ""}`}
                      onClick={() => { setSelectedResumeId(r.id); setResumeOpen(false); }}
                    >
                      {r.title}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-buttons">
          <button onClick={handleSubmit}>{initialApp ? "Save Changes" : "Create"}</button>
          <button className="secondary" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
